import * as Sentry from '@sentry/react';

/**
 * Keeps React from crashing when something outside React has already moved the DOM node React is
 * about to remove or insert before.
 *
 * Browser translators (Chrome for iOS, Google Translate) and extensions rewrite text nodes React
 * owns; `<meta name="google" content="notranslate">` only stops Chrome's own prompt. On React's
 * next commit, `removeChild` / `insertBefore` then throw NotFoundError ("The object can not be
 * found here", "not a child of this node"): DASHBOARD-WEB-5 / -8 / -9. The throw escapes React's
 * commit phase and unmounts the whole dashboard.
 *
 * When the parent does not match, the call becomes a no-op. A breadcrumb and a Sentry log line
 * keep the signal, so a real React bug that trips this guard still shows up.
 */
export function installDomMutationGuard() {
  if (typeof Node !== 'function' || !Node.prototype) {
    return;
  }

  const originalRemoveChild = Node.prototype.removeChild;
  const originalInsertBefore = Node.prototype.insertBefore;

  if (originalRemoveChild.__domMutationGuard) {
    return;
  }

  const report = (operation, parent, child) => {
    const data = { operation, parent: parent.nodeName, child: child?.nodeName ?? null };

    Sentry.addBreadcrumb({ category: 'dom.guard', level: 'warning', data });
    Sentry.logger?.warn('dom_mutation_guard', data);
  };

  function removeChild(child) {
    if (child.parentNode !== this) {
      report('removeChild', this, child);

      return child;
    }

    return originalRemoveChild.call(this, child);
  }

  function insertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      report('insertBefore', this, referenceNode);

      return newNode;
    }

    return originalInsertBefore.call(this, newNode, referenceNode);
  }

  removeChild.__domMutationGuard = true;

  Node.prototype.removeChild = removeChild;
  Node.prototype.insertBefore = insertBefore;
}
